import { motion } from 'motion/react';
import { SignUpButton, TalkToFoundersButton } from './CTAButtons';

import { usePricing } from '../hooks/usePricing';
import { formatPlanPrice } from '../lib/pricing';
import { JsonLd } from './seo/JsonLd';
import { buildPricingPageSchema } from '../lib/schema';

const PLAN_FEATURES: Record<string, string[]> = {
  starter_monthly: ['Access to all top models', 'Single creator seat', 'Email support'],
  pro_monthly: ['Rhapsody engine', 'Team workspace (up to 5 seats)', 'Priority support'],
};

const ease = [0.16, 1, 0.3, 1] as const;

const tierItem = {
  hidden: { opacity: 0, y: 52, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 1, ease } },
};

function TierCard({ children, featured = false }: { children: React.ReactNode; featured?: boolean }) {
  return (
    /* The outer element is the hover target and never moves. If the card itself owned
       :hover, lifting it 10px would slide its own bottom edge out from under the cursor,
       ending the hover, dropping it back, and oscillating for as long as the pointer
       rested near an edge. */
    <motion.div
      className="group h-full"
      variants={tierItem}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
    >
      {/* tier class retained for .tier.featured::before gradient-border pseudo-element */}
      <div
        className={`tier bg-surface-container rounded-[6px] p-8 flex flex-col h-full min-h-[520px] relative transition-[transform,background,box-shadow] duration-[600ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] group-hover:bg-surface-container-high group-hover:-translate-y-[10px] group-hover:[box-shadow:0_40px_80px_-24px_rgba(0,0,0,0.7)] ${featured ? 'featured' : ''}`}
      >
        {children}
      </div>
    </motion.div>
  );
}

/**
 * Self-serve tiers lead with signup — the plan is buyable without us — and keep talking
 * to sales beside it. The buttons grow from their natural width rather than being
 * squeezed: `.btn` never wraps its label, so on a card too narrow for both they wrap to
 * their own rows instead of overflowing.
 */
function TierActions({ children }: { children: React.ReactNode }) {
  return <div className="mt-6 flex flex-wrap gap-2.5">{children}</div>;
}

const TIER_BTN = 'grow shrink-0 basis-auto justify-center';

type PricingProps = {
  titleAs?: 'h1' | 'h2';
};

export function Pricing({ titleAs = 'h1' }: PricingProps) {
  const Heading = titleAs;
  const { status, plans, retry } = usePricing();

  return (
    <section className="section band-light low" id="pricing">
      {titleAs === 'h1' && <JsonLd data={buildPricingPageSchema(plans)} />}
      <div className="container">
        <motion.div
          className="flex justify-between items-end gap-10 mb-14 max-md:flex-col max-md:items-start"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 1, ease }}
        >
          <Heading className="home-section-title m-0 max-w-[16ch]">
            Pricing that scales<br />with the production.
          </Heading>
          <p className="body-lg max-w-[42ch]">
            Monthly credits for creators and teams. Custom usage agreements for Enterprise.
          </p>
        </motion.div>

        <div className="grid grid-cols-3 gap-5 max-lg:grid-cols-2 max-sm:grid-cols-1">
          {status !== 'ready' && (
            <div className="min-h-[520px] p-8 col-span-2 max-sm:col-span-1" role="status" aria-live="polite">
              <p>{status === 'loading' ? 'Loading current plans…' : 'Pricing is temporarily unavailable. Please try again.'}</p>
              {status === 'error' && <button type="button" className="btn btn-ghost mt-6" onClick={retry}>Retry</button>}
            </div>
          )}
          {plans.map((plan, index) => (
            <TierCard key={plan.id} featured={plan.highlighted}>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary mb-[14px] flex gap-[10px] items-center">
                <span>TIER {String(index + 1).padStart(2, '0')}</span><span>· {plan.name}</span>
              </div>
              <h3 className="font-display text-[36px] font-medium m-0 mb-[6px] tracking-[-0.015em]">{plan.name}</h3>
              <p className="text-on-surface-variant text-[15px] leading-[1.5] m-0 mb-6 max-w-[32ch]">{plan.description}</p>
              <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-on-surface-muted mb-2">
                {plan.monthlyCredits.toLocaleString('en-US')} credits / month
              </div>
              <div className="font-display text-[40px] font-light text-on-surface tracking-[-0.02em] mb-[6px]">
                {formatPlanPrice(plan)}
                {plan.taxLabel && <sub className="font-mono text-[11px] text-on-surface-muted tracking-[0.14em] uppercase align-middle ml-2">{plan.taxLabel}</sub>}
              </div>
              <div className="text-on-surface-muted text-sm">Billed {plan.billingInterval}</div>
              <ul className="list-none p-0 mt-6 flex flex-col gap-[14px] flex-1">
                {(PLAN_FEATURES[plan.code] ?? []).map(feature => (
                  <li key={feature} className="text-[14px] text-on-surface-variant flex gap-3 leading-[1.5]">{feature}</li>
                ))}
              </ul>
              <TierActions>
                <SignUpButton variant={plan.highlighted ? 'primary' : 'ghost'} analyticsLocation="pricing" className={TIER_BTN} />
                <TalkToFoundersButton variant="ghost" analyticsLocation="pricing" className={TIER_BTN} />
              </TierActions>
            </TierCard>
          ))}

          <TierCard>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary mb-[14px] flex gap-[10px] items-center">
              <span>TIER {String((plans.length || 2) + 1).padStart(2, '0')}</span><span>· ENTERPRISE</span>
            </div>
            <h3 className="font-display text-[36px] font-medium m-0 mb-[6px] tracking-[-0.015em]">Enterprise</h3>
            <p className="text-on-surface-variant text-[15px] leading-[1.5] m-0 mb-6 max-w-[32ch]">
              Custom capacity, infrastructure, and support. Usage and retry terms are defined in your agreement.
            </p>
            <div className="font-display text-[40px] font-light text-on-surface tracking-[-0.02em] mb-[6px]">
              Custom<sub className="font-mono text-[11px] text-on-surface-muted tracking-[0.14em] uppercase align-middle ml-2">talk to us</sub>
            </div>
            <ul className="list-none p-0 mt-6 flex flex-col gap-[14px] flex-1">
              {['Agreed render capacity, dedicated cluster', 'Private continuity models + VPC option', 'API, webhooks, pipeline integrations', 'Named engineer + 24/7 support'].map(f => (
                <li key={f} className="text-[14px] text-on-surface-variant flex gap-3 leading-[1.5]">{f}</li>
              ))}
            </ul>
            <TierActions>
              {/* Enterprise is not self-serve, so its one button owns the row. */}
              <TalkToFoundersButton variant="ghost" analyticsLocation="pricing" className={`${TIER_BTN} w-full`} />
            </TierActions>
          </TierCard>
        </div>
      </div>
    </section>
  );
}
